import { HeadingSize, PageHeading } from "@via/components/page-heading.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

interface CollapsibleContainerProps {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  size?: HeadingSize;
}

const CollapsibleContainer: FC<CollapsibleContainerProps> = ({
  title,
  children,
  defaultOpen = false,
  isOpen,
  onToggle,
  size = HeadingSize.Small,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const actualOpen = isOpen !== undefined ? isOpen : internalOpen;

  const toggleOpen = useCallback(() => {
    const newOpen = !actualOpen;
    if (isOpen === undefined) {
      setInternalOpen(newOpen);
    }
    if (onToggle) {
      onToggle(newOpen);
    }
  }, [actualOpen, isOpen, onToggle]);

  useEffect(() => {
    if (isOpen !== undefined) {
      setInternalOpen(isOpen);
    }
  }, [isOpen]);

  return (
    <div className="mx-auto my-4 w-full shadow-md ring-1 ring-gray-900/5 mx-0 rounded-lg">
      <div
        className="mx-auto flex cursor-pointer items-center justify-between py-3 lg:px-6 md:px-6 px-3 lg:col-span-2 lg:row-span-2 lg:row-end-2"
        onClick={toggleOpen}>
        <PageHeading title={title} size={size} />
        <svg
          className={classNames(
            "transform transition-transform duration-300",
            actualOpen ? "rotate-180" : ""
          )}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M12 15L6 9L18 9L12 15Z" fill="currentColor" />
        </svg>
      </div>
      {actualOpen && (
        <div className="mx-auto bg-white pb-4 mx-0 rounded-lg lg:px-6 md:px-6 px-3 lg:col-span-2 lg:row-span-2 lg:row-end-2">
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleContainer;
